import React, { MutableRefObject, useRef } from 'react';
import * as R from 'ramda';

import { ExpandableToggleContainer } from 'src/components/organisms/ProductDescription/components/ExpandableToggleContainer/ExpandableToggleContainer';
import {
  InteractionErrorTrackingEvent,
  InteractionTrackingEvent,
  ProgressTrackingEvent,
  SelectionPayload,
  TrackedEvents,
} from 'src/redux/personalisation';
import { FieldEventType, ProductOption } from 'src/types';
import { MIN_PERSONALISATIONS_OPTIONS_FOR_EXPANDING } from '../../../ProductInfo/constants';
import { InputOnChangeArgs } from '../../components/Input';
import { PERSONALISATION_OPTIONS_ID } from '../../constants/elementIds';
import { useExpandablePersonalisationOptions } from '../../hooks/useExpandablePersonalisationOptions';
import { getFieldPosition, getProgressMilestone } from '../../utils';
import { PersonalisationOption } from './PersonalisationOption';
import * as styles from '../../styles/Personalisation.styles';

interface Props {
  displayValidation?: boolean;
  isExpanded?: boolean;
  options: ProductOption[];
  outstandingSelections: ProductOption[];
  totalRequiredPersonalisations: number;
  trackedEvents: TrackedEvents;
  deselectPersonalisation: (selection: SelectionPayload) => void;
  selectPersonalisation: (selection: SelectionPayload) => void;
  toggleExpanded: () => void;
  trackInteraction: (payload: InteractionTrackingEvent) => void;
  trackInteractionError: (payload: InteractionErrorTrackingEvent) => void;
  trackProgressMilestone: (payload: ProgressTrackingEvent) => void;
}

type HandleInputSelectionFnParams = ProductOption &
  Pick<
    Props,
    | 'deselectPersonalisation'
    | 'options'
    | 'outstandingSelections'
    | 'selectPersonalisation'
    | 'totalRequiredPersonalisations'
    | 'trackedEvents'
    | 'trackInteraction'
    | 'trackInteractionError'
    | 'trackProgressMilestone'
  > & {
    index: number;
  };

const handleInputSelection = R.curry(
  (
    {
      deselectPersonalisation,
      id,
      name,
      options,
      outstandingSelections,
      required,
      selectPersonalisation,
      totalRequiredPersonalisations,
      trackedEvents,
      trackInteraction,
      trackInteractionError,
      trackProgressMilestone,
    }: HandleInputSelectionFnParams,
    { value, isValid }: InputOnChangeArgs,
  ) => {
    const payload: SelectionPayload = {
      optionId: id,
      optionValue: {
        id,
        name: value,
        priceModifier: null,
        isCustomInput: true,
        isValid,
      },
      type: 'single-selection',
    };

    if (!value) {
      return deselectPersonalisation(payload);
    }

    selectPersonalisation(payload);

    const hasTrackedCharacterLimitError = trackedEvents.error['Character Limit Error'][name];

    if (isValid) {
      trackInteraction({
        field_position: getFieldPosition(options, { id }),
        field_name: name,
        field_action: 'Field Input',
        field_type: 'input field',
        field_value: null,
        required_field: required,
      });
    } else if (!hasTrackedCharacterLimitError) {
      trackInteractionError({
        field_position: getFieldPosition(options, { id }),
        field_name: name,
        field_action: 'Character Limit Error',
        field_type: 'input field',
        required_field: required,
      });
    }

    const milestone = getProgressMilestone({
      totalRequiredPersonalisations,
      totalOutstandingSelections: outstandingSelections.length,
      trackedEvents,
      fieldName: name,
      isRequiredPersonalisation: required,
    });

    if (milestone) {
      trackProgressMilestone({
        field_position: getFieldPosition(options, { id }),
        field_name: name,
        field_action: milestone,
        field_type: 'input field',
        field_value: null,
        required_field: true,
      });
    }
  },
);

export const PersonalisationOptions = ({
  displayValidation,
  isExpanded,
  options,
  totalRequiredPersonalisations,
  outstandingSelections,
  trackedEvents,
  deselectPersonalisation,
  selectPersonalisation,
  toggleExpanded,
  trackInteraction,
  trackProgressMilestone,
  trackInteractionError,
}: Props) => {
  const wrapperRef: MutableRefObject<HTMLUListElement | null> = useRef(null);
  const fourthElementRef: MutableRefObject<HTMLLIElement | null> = useRef(null);
  const expandable = options.length > 4;

  const { unexpandedHeight } = useExpandablePersonalisationOptions(
    wrapperRef,
    fourthElementRef,
    expandable,
  );

  const handleFocus = (option?: ProductOption, fieldType?: FieldEventType) => {
    if (fieldType && option && !trackedEvents.focus[option.name]) {
      trackInteraction({
        field_position: getFieldPosition(options, option),
        field_name: option.name,
        field_action: 'Field Focus',
        field_type: fieldType,
        field_value: null,
        required_field: option.required,
      });
    }

    if (!isExpanded && options.length >= MIN_PERSONALISATIONS_OPTIONS_FOR_EXPANDING) {
      toggleExpanded();
    }
  };

  return (
    <ExpandableToggleContainer
      expandable={expandable}
      showButton={false}
      isExpandedOverride={isExpanded}
      toggleContainerId={PERSONALISATION_OPTIONS_ID}
      minHeight={unexpandedHeight}
      styles={styles.personalisationOptionsExpandable}
    >
      <ul
        id={PERSONALISATION_OPTIONS_ID}
        data-testid={PERSONALISATION_OPTIONS_ID}
        ref={wrapperRef}
        css={styles.personalisationOptions}
      >
        {options.map((option, index) => {
          if (option.type === 'selection') {
            return (
              <PersonalisationOption
                displayValidation={displayValidation}
                listItemRef={index === 3 ? fourthElementRef : null}
                key={option.id}
                option={option}
                wrapperRef={wrapperRef}
                onFocus={handleFocus}
                isExpanded={isExpanded}
                toggleExpanded={toggleExpanded}
              />
            );
          }

          return (
            <PersonalisationOption
              displayValidation={displayValidation}
              isExpanded={isExpanded}
              listItemRef={index === 3 ? fourthElementRef : null}
              wrapperRef={wrapperRef}
              key={option.id}
              option={option}
              onFocus={handleFocus}
              onChange={handleInputSelection({
                deselectPersonalisation,
                options,
                outstandingSelections,
                selectPersonalisation,
                totalRequiredPersonalisations,
                trackedEvents,
                trackInteraction,
                trackInteractionError,
                trackProgressMilestone,
                ...option,
                index,
              })}
              toggleExpanded={toggleExpanded}
            />
          );
        })}
      </ul>
    </ExpandableToggleContainer>
  );
};
